import { ChatCircle, Check, Tray, Warning, X } from '@phosphor-icons/react';
import {
  getListApprovalsQueryKey,
  useDecideApproval,
  useListApprovals,
} from '@reelplicate/api-client/approvals';
import type { ApprovalDto } from '@reelplicate/api-client/model';
import { useListPersonas } from '@reelplicate/api-client/personas';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  Avatar,
  Badge,
  Button,
  EmptyState,
  Field,
  Inline,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SegmentedControl,
  SegmentedControlItem,
  Skeleton,
  Stack,
  TextArea,
  toast,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router';

import { humanize } from './catalogue-ui';
import { ItemMedia } from './editor/run/item-media';
import { DraftReviewModal, type ReviewTab } from './review/draft-review-modal';

type List = 'PENDING' | 'DECIDED';

/**
 * `/flows/drafts` - the review queue. Flows with Human approval park their
 * drafts here; approving sends one on through its flow.
 */
export function DraftsPage() {
  const queryClient = useQueryClient();
  const { data: workspaces } = useListWorkspaces();
  const workspaceId = workspaces?.[0]?.id ?? '';
  const [list, setList] = useState<List>('PENDING');
  const { data: allApprovals } = useListApprovals(
    workspaceId,
    { status: list },
    { query: { enabled: Boolean(workspaceId) } },
  );
  // `?run=` narrows the queue to one run (a notification's "Review" link).
  const [searchParams, setSearchParams] = useSearchParams();
  const runId = searchParams.get('run');
  const approvals = runId
    ? allApprovals?.filter((approval) => approval.runId === runId)
    : allApprovals;
  const runFlowName = allApprovals?.find(
    (approval) => approval.runId === runId,
  )?.flowName;
  const setParam = (key: string, value: string | null) =>
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });
  // A draft open in review: `?view=` (a card, or Home's "Needs your review"),
  // or `?approval=` on its Comments tab (a mention or reply notification).
  const viewId = searchParams.get('view') ?? searchParams.get('approval');
  const viewing = allApprovals?.find((approval) => approval.id === viewId);
  const [slide, setSlide] = useState(0);
  const { data: personas } = useListPersonas(workspaceId, {
    query: { enabled: Boolean(workspaceId) },
  });
  const personaOf = (approval: ApprovalDto) => {
    const id = approval.item.payload.personaId;
    return typeof id === 'string'
      ? personas?.items.find((persona) => persona.id === id)
      : undefined;
  };
  const [reviewTab, setReviewTab] = useState<ReviewTab>(
    searchParams.get('approval') ? 'comments' : 'slides',
  );
  const open = (approvalId: string, at: number, tab: ReviewTab) => {
    setSlide(at);
    setReviewTab(tab);
    setParam('view', approvalId);
  };
  const close = () =>
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('view');
      next.delete('approval');
      return next;
    });
  const loaded = Boolean(approvals);
  useEffect(() => {
    if (!viewId || !loaded) return;
    document.getElementById(`draft-${viewId}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [viewId, loaded]);
  const decide = useDecideApproval();
  const [busy, setBusy] = useState(false);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: getListApprovalsQueryKey(workspaceId) });

  const review = async (
    targets: ApprovalDto[],
    decision: 'APPROVE' | 'REJECT',
    note?: string,
  ) => {
    setBusy(true);
    let done = 0;
    try {
      for (const approval of targets) {
        await decide.mutateAsync({
          id: workspaceId,
          approvalId: approval.id,
          data: { decision, note },
        });
        done++;
      }
    } catch {
      // Shown as a toast; the ones before it went through.
    } finally {
      setBusy(false);
      await refresh();
    }
    if (done) {
      toast.success(
        decision === 'APPROVE'
          ? `${done} ${done === 1 ? 'draft' : 'drafts'} approved`
          : `${done} ${done === 1 ? 'draft' : 'drafts'} rejected`,
      );
    }
  };

  return (
    <div className="flex flex-col gap-6 p-8">
      <header className="flex flex-wrap items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h1 className="text-heading-h1 text-text-primary">Drafts</h1>
          <p className="text-body-md text-text-secondary">
            What your flows made, waiting for a person to approve it.
          </p>
        </div>
        {list === 'PENDING' && approvals && approvals.length > 1 && (
          <Button
            leadingIcon={Check}
            loading={busy}
            onClick={() => void review(approvals, 'APPROVE')}
          >
            Approve all {approvals.length}
          </Button>
        )}
      </header>

      <SegmentedControl
        aria-label="Drafts"
        value={list}
        onValueChange={(v) => setList(v as List)}
      >
        <SegmentedControlItem value="PENDING">Waiting for review</SegmentedControlItem>
        <SegmentedControlItem value="DECIDED">Reviewed</SegmentedControlItem>
      </SegmentedControl>

      {runId && (
        <Inline gap={3} alignY="center" wrap>
          <p className="text-body-sm text-text-secondary">
            Showing drafts from one run{runFlowName ? ` of ${runFlowName}` : ''}.
          </p>
          <Button variant="ghost" size="sm" onClick={() => setParam('run', null)}>
            Show all
          </Button>
        </Inline>
      )}

      {!approvals ? (
        <Skeleton className="h-40" shape="rectangle" />
      ) : approvals.length === 0 ? (
        <EmptyState
          icon={Tray}
          title={list === 'PENDING' ? 'Nothing to review' : 'Nothing reviewed yet'}
          description={
            list === 'PENDING'
              ? 'When a flow with Human approval runs, its drafts wait here for you.'
              : 'Drafts you approve or reject show up here.'
          }
          actions={
            <Button asChild variant="outline">
              <RouterLink to="/flows">Go to flows</RouterLink>
            </Button>
          }
        />
      ) : (
        <ul className="grid grid-cols-1 gap-4 laptop:grid-cols-2 desktop:grid-cols-3">
          {approvals.map((approval) => (
            <li key={approval.id}>
              <DraftCard
                approval={approval}
                persona={personaOf(approval)}
                onOpen={(slide) => open(approval.id, slide, 'slides')}
              />
            </li>
          ))}
        </ul>
      )}
      {viewing && (
        <DraftReviewModal
          workspaceId={workspaceId}
          approval={viewing}
          slide={slide}
          onSlideChange={setSlide}
          tab={reviewTab}
          onTabChange={setReviewTab}
          busy={busy}
          onDecide={(d, note) => {
            close();
            void review([viewing], d, note);
          }}
          reject={
            <RejectButton
              busy={busy}
              onReject={(note) => {
                close();
                void review([viewing], 'REJECT', note);
              }}
            />
          }
          onClose={close}
        />
      )}
    </div>
  );
}

function DraftCard({
  approval,
  persona,
  onOpen,
}: {
  approval: ApprovalDto;
  /** The persona it shows, when it was made for one. */
  persona?: { name: string; avatarUrl: string | null };
  /** Open it in review at a slide (0-based). */
  onOpen: (slide: number) => void;
}) {
  const { item } = approval;
  // Reels and carousels carry their script inside.
  const payload = (
    typeof item.payload.script === 'object' && item.payload.script
      ? item.payload.script
      : item.payload
  ) as Record<string, unknown>;
  const angle = typeof payload.angle === 'string' ? humanize(payload.angle) : null;
  const body = typeof payload.body === 'string' ? payload.body : null;
  const cta = typeof payload.cta === 'string' ? payload.cta : null;
  const caption = typeof item.payload.caption === 'string' ? item.payload.caption : null;
  const slides = Array.isArray(item.payload.slides)
    ? (item.payload.slides as { text?: string }[])
    : [];
  const noText = slides.filter((s) => !s.text?.trim()).length;

  // A preview: the whole card opens the draft in review, where it's decided.
  return (
    <article
      id={`draft-${approval.id}`}
      aria-label={item.summary}
      className="bg-bg-surface border-border-subtle hover:border-border-default focus-within:border-border-default relative flex h-full flex-col gap-3 rounded-xl border p-5 transition-colors"
    >
      <div className="flex items-center gap-2">
        {persona ? (
          // Made for a persona: who it's for says more than which flow made it.
          <span className="bg-bg-muted text-label-sm text-text-primary inline-flex h-6 min-w-0 items-center gap-1.5 rounded-full pr-2.5 pl-0.5">
            <Avatar
              size={20}
              name={persona.name}
              src={persona.avatarUrl ?? undefined}
              alt=""
            />
            <span className="truncate">{persona.name}</span>
          </span>
        ) : (
          <span className="text-caption text-text-secondary truncate">
            {approval.flowName}
          </span>
        )}
        {approval.runMode === 'TEST' && (
          // Approving a test draft keeps it in Media; nothing is posted.
          <Badge>Test run</Badge>
        )}
        {angle && <Badge tone="brand">{angle}</Badge>}
        {approval.status !== 'PENDING' && (
          <Badge
            tone={approval.status === 'APPROVED' ? 'success' : 'danger'}
            dot
            className="ml-auto"
          >
            {approval.status === 'APPROVED' ? 'Approved' : 'Rejected'}
          </Badge>
        )}
      </div>
      {/* Above the card's target: a slide opens at that slide. */}
      <ItemMedia item={item} compact onOpen={onOpen} />
      <h2 className="text-heading-h4 text-text-primary">
        {/* One target for the whole card (its ::after covers it). */}
        <button
          type="button"
          onClick={() => onOpen(0)}
          className="text-left outline-none after:absolute after:inset-0 after:rounded-xl focus-visible:after:shadow-focus-ring"
        >
          {item.summary}
        </button>
      </h2>
      {caption ? (
        <p className="text-body-sm text-text-secondary line-clamp-4">{caption}</p>
      ) : (
        <>
          {body && <p className="text-body-sm text-text-secondary">{body}</p>}
          {cta && <p className="text-body-sm-strong text-text-primary">{cta}</p>}
        </>
      )}
      {approval.note && (
        <p className="text-caption text-text-secondary">Note: {approval.note}</p>
      )}
      <Inline gap={2} alignY="center" className="mt-auto pt-2">
        {noText > 0 && (
          <span className="text-caption text-status-warning-fg inline-flex items-center gap-1">
            <Warning aria-hidden className="size-3.5" />
            {noText === 1 ? '1 slide has no text' : `${noText} slides have no text`}
          </span>
        )}
        <span className="flex-1" />
        {approval.mentioned && (
          <Badge tone="brand" dot>
            Mentioned
          </Badge>
        )}
        <span
          className="text-caption text-text-secondary inline-flex items-center gap-1"
          aria-label={`${approval.commentCount} ${approval.commentCount === 1 ? 'comment' : 'comments'}`}
        >
          <ChatCircle aria-hidden className="size-4" />
          {approval.commentCount}
        </span>
      </Inline>
    </article>
  );
}

/** Reject, with an optional note on why. */
function RejectButton({
  busy,
  onReject,
}: {
  busy: boolean;
  onReject: (note?: string) => void;
}) {
  const [note, setNote] = useState('');
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" leadingIcon={X} disabled={busy}>
          Reject
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" aria-label="Reject draft" className="w-80 p-3">
        <Stack gap={3}>
          <Field label="Why?" optional>
            <TextArea value={note} onChange={(event) => setNote(event.target.value)} />
          </Field>
          <Button size="sm" variant="danger" onClick={() => onReject(note)}>
            Reject draft
          </Button>
        </Stack>
      </PopoverContent>
    </Popover>
  );
}
